import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./Customer.css";
import Modal from "../components/Modal";
import AddUser from "../components/AddUser";
import EditUser from "../components/EditUser";
import { api } from "../config/api";
import { getAuthData } from "../utils/auth";

interface LotData {
  lotId: string;
  displayLotId: string;
  companyName: string;
  address: string;
  lotName: string;
  lotCapacity: number;
  hasEvCharging: boolean;
  lotStatus: string;
  currentOccupancy: number;
  createdOn: string;
  modifiedOn: string;
  modifiedBy: string;
  allowBetaAi: boolean;
  developmentLot: boolean;
  lotLongitude?: number;
  lotLatitude?: number;
  addressLastUpdatedOn?: string;
  owner?: {
    userId: string;
    name: string;
    email: string;
  };
  ownerId?: string;
}

interface UserData {
  userId: string;
  displayUserId: string;
  name: string;
  email: string;
  phoneNo: string;
  role: string;
  isVerified: boolean;
  isBanned: boolean;
  bannedAt: string | null;
  createdOn: string;
  createdBy: string;
  modifiedOn: string;
  modifiedBy: string;
  isDeleted: boolean;
}

const Customer: React.FC = () => {
  const { lotId } = useParams<{ lotId: string }>();
  const navigate = useNavigate();

  const [lot, setLot] = useState<LotData | null>(null);
  const [owner, setOwner] = useState<UserData | null>(null);
  const [operators, setOperators] = useState<UserData[]>([]);
  const [staff, setStaff] = useState<UserData[]>([]);
  const [editableFields, setEditableFields] = useState<Partial<LotData>>({});
  const [editMode, setEditMode] = useState(false);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [addUserType, setAddUserType] = useState<'operator' | 'staff' | 'owner' | null>(null);
  const [showDeleteUserModal, setShowDeleteUserModal] = useState<{ type: 'operator' | 'staff', userId: string } | null>(null);
  const [editingUser, setEditingUser] = useState<UserData | null>(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirmationModalData, setConfirmationModalData] = useState<{
    field: keyof LotData;
    value: boolean;
    title: string;
    message: string;
  } | null>(null);
  const [lotDevices, setLotDevices] = useState<any[]>([]);

  // Get current user ID from auth
  const getCurrentUserId = (): string => {
    const authData = getAuthData();
    return authData?.userId || "1"; // Fallback to "1" if no auth data
  };

  // Address editing state
  const [streetNumber, setStreetNumber] = useState("");
  const [streetName, setStreetName] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("Canada");
  const [provinceState, setProvinceState] = useState("");
  const [postalCode, setPostalCode] = useState("");

  // Address validation state
  const [streetNumberError, setStreetNumberError] = useState(false);
  const [streetNameError, setStreetNameError] = useState(false);
  const [cityError, setCityError] = useState(false);
  const [provinceStateError, setProvinceStateError] = useState(false);
  const [postalCodeError, setPostalCodeError] = useState(false);

  // Canadian provinces
  const canadianProvinces = [
    { value: "AB", label: "Alberta" },
    { value: "BC", label: "British Columbia" },
    { value: "MB", label: "Manitoba" },
    { value: "NB", label: "New Brunswick" },
    { value: "NL", label: "Newfoundland and Labrador" },
    { value: "NS", label: "Nova Scotia" },
    { value: "NT", label: "Northwest Territories" },
    { value: "NU", label: "Nunavut" },
    { value: "ON", label: "Ontario" },
    { value: "PE", label: "Prince Edward Island" },
    { value: "QC", label: "Quebec" },
    { value: "SK", label: "Saskatchewan" },
    { value: "YT", label: "Yukon" }
  ];

  // US states
  const usStates = [
    { value: "AL", label: "Alabama" },
    { value: "AK", label: "Alaska" },
    { value: "AZ", label: "Arizona" },
    { value: "AR", label: "Arkansas" },
    { value: "CA", label: "California" },
    { value: "CO", label: "Colorado" },
    { value: "CT", label: "Connecticut" },
    { value: "DE", label: "Delaware" },
    { value: "DC", label: "District of Columbia" },
    { value: "FL", label: "Florida" },
    { value: "GA", label: "Georgia" },
    { value: "HI", label: "Hawaii" },
    { value: "ID", label: "Idaho" },
    { value: "IL", label: "Illinois" },
    { value: "IN", label: "Indiana" },
    { value: "IA", label: "Iowa" },
    { value: "KS", label: "Kansas" },
    { value: "KY", label: "Kentucky" },
    { value: "LA", label: "Louisiana" },
    { value: "ME", label: "Maine" },
    { value: "MD", label: "Maryland" },
    { value: "MA", label: "Massachusetts" },
    { value: "MI", label: "Michigan" },
    { value: "MN", label: "Minnesota" },
    { value: "MS", label: "Mississippi" },
    { value: "MO", label: "Missouri" },
    { value: "MT", label: "Montana" },
    { value: "NE", label: "Nebraska" },
    { value: "NV", label: "Nevada" },
    { value: "NH", label: "New Hampshire" },
    { value: "NJ", label: "New Jersey" },
    { value: "NM", label: "New Mexico" },
    { value: "NY", label: "New York" },
    { value: "NC", label: "North Carolina" },
    { value: "ND", label: "North Dakota" },
    { value: "OH", label: "Ohio" },
    { value: "OK", label: "Oklahoma" },
    { value: "OR", label: "Oregon" },
    { value: "PA", label: "Pennsylvania" },
    { value: "RI", label: "Rhode Island" },
    { value: "SC", label: "South Carolina" },
    { value: "SD", label: "South Dakota" },
    { value: "TN", label: "Tennessee" },
    { value: "TX", label: "Texas" },
    { value: "UT", label: "Utah" },
    { value: "VT", label: "Vermont" },
    { value: "VA", label: "Virginia" },
    { value: "WA", label: "Washington" },
    { value: "WV", label: "West Virginia" },
    { value: "WI", label: "Wisconsin" },
    { value: "WY", label: "Wyoming" }
  ];

  // Parse address into components for editing
  const parseAddressForEditing = (fullAddress: string) => {
    const parts = fullAddress.split(', ');
    if (parts.length >= 5) {
      setStreetNumber(parts[0] || "");
      setStreetName(parts[1] || "");
      setCity(parts[2] || "");
      setProvinceState(parts[3] || "");
      setPostalCode(parts[4] || "");
      setCountry(parts[5] || "Canada");
    } else {
      // If address doesn't match expected format, try to parse what we can
      setStreetNumber("");
      setStreetName("");
      setCity("");
      setProvinceState("");
      setPostalCode("");
      setCountry("Canada");
    }
  };

  // Validate address components
  const validateStreetNumber = (value: string): boolean => {
    if (value.trim().length === 0) return true; // Don't show error for empty fields
    return /^[0-9]+$/.test(value.trim()) && value.length <= 10;
  };

  const validateStreetName = (value: string): boolean => {
    if (value.trim().length === 0) return true; // Don't show error for empty fields
    return value.length <= 50;
  };

  const validateCity = (value: string): boolean => {
    if (value.trim().length === 0) return true; // Don't show error for empty fields
    return value.length <= 30;
  };

  const validatePostalCode = (value: string): boolean => {
    if (value.trim().length === 0) return true; // Don't show error for empty fields
    if (country === "Canada") {
      // Canadian postal code format: A1A 1A1
      return /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/.test(value);
    } else {
      // US ZIP code format: 12345 or 12345-6789
      return /^\d{5}(-\d{4})?$/.test(value);
    }
  };

  // Handle address component changes
  const handleAddressComponentChange = (component: string, value: string) => {
    let updatedStreetNumber = streetNumber;
    let updatedStreetName = streetName;
    let updatedCity = city;
    let updatedCountry = country;
    let updatedProvinceState = provinceState;
    let updatedPostalCode = postalCode;

    switch (component) {
      case 'streetNumber':
        // Only allow numbers
        const numericValue = value.replace(/[^0-9]/g, '');
        updatedStreetNumber = numericValue;
        setStreetNumber(numericValue);
        setStreetNumberError(!validateStreetNumber(numericValue));
        break;
      case 'streetName':
        updatedStreetName = value;
        setStreetName(value);
        setStreetNameError(!validateStreetName(value));
        break;
      case 'city':
        updatedCity = value;
        setCity(value);
        setCityError(!validateCity(value));
        break;
      case 'country':
        updatedCountry = value;
        updatedProvinceState = ""; // Reset province/state when country changes
        setCountry(value);
        setProvinceState("");
        break;
      case 'provinceState':
        updatedProvinceState = value;
        setProvinceState(value);
        setProvinceStateError(!value);
        break;
      case 'postalCode':
        let formattedValue = value;
        if ((component === 'postalCode' ? updatedCountry : country) === "Canada") {
          // Format Canadian postal code (remove spaces/hyphens, then add space)
          formattedValue = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
          if (formattedValue.length > 3) {
            formattedValue = formattedValue.substring(0, 3) + ' ' + formattedValue.substring(3, 6);
          }
        } else {
          // US ZIP code - allow numbers and hyphens
          formattedValue = value.replace(/[^0-9-]/g, '');
        }
        updatedPostalCode = formattedValue;
        setPostalCode(formattedValue);
        setPostalCodeError(!validatePostalCode(formattedValue));
        break;
    }

    // Update the combined address and mark as unsaved
    const combinedAddress = [
      updatedStreetNumber.trim(),
      updatedStreetName.trim(),
      updatedCity.trim(),
      updatedProvinceState,
      updatedPostalCode.trim(),
      updatedCountry
    ].join(', ');

    setEditableFields((prev) => ({
      ...prev,
      address: combinedAddress
    }));
    setUnsavedChanges(true);
  };

  const fetchLot = async (id: string) => {
    try {
      const response = await api.get(`/parkinglots/get-by-id/${id}`);
      const fullLotData = response.data;

      if (fullLotData.success) {
        setLot(fullLotData.data);
        console.log('Lot data loaded:', fullLotData.data);

        // Set editable fields
        const initialEditableFields = {
          companyName: fullLotData.data.companyName,
          address: fullLotData.data.address,
          lotName: fullLotData.data.lotName,
          lotCapacity: fullLotData.data.lotCapacity,
          allowBetaAi: fullLotData.data.allowBetaAi === true,
          developmentLot: fullLotData.data.developmentLot === true,
        };
        setEditableFields(initialEditableFields);

        // Parse the address for editing
        if (fullLotData.data.address) {
          parseAddressForEditing(fullLotData.data.address);
        }

        // Fetch owner data if ownerId is available
        if (fullLotData.data.ownerId) {
          const ownerResponse = await api.get(`/users/get-user-by-id/${fullLotData.data.ownerId}`);
          const ownerData = ownerResponse.data;

          if (ownerData.success) {
            setOwner(ownerData.data);
            console.log('Owner data loaded:', ownerData.data);
          } else {
            console.error('Failed to fetch owner data:', ownerData.message);
          }
        }

        setErrorMessage(null);
      } else {
        console.error('Failed to fetch lot data:', fullLotData.message);
      }
    } catch (error) {
      console.error('Error fetching lot data:', error);
      setErrorMessage('Failed to load lot data. Please try again.');
    }
  };

  const fetchOperators = async (id: string) => {
    try {
      const response = await api.get(`/parkinglots/get-all-operators/${id}`);
      const data = response.data;
      if (data.success) {
        setOperators(data.data);
      } else {
        console.error('Failed to fetch operators:', data.message);
      }
    } catch (error: any) {
      if (error.response?.status === 404) {
        // No operators found is normal, just set empty array
        setOperators([]);
      } else {
        console.error('Error fetching operators:', error);
      }
    }
  };

  const fetchStaff = async (id: string) => {
    try {
      const responseStaff = await api.get(`/parkinglots/get-all-staff/${id}`);
      const dataStaff = responseStaff.data;
      if (dataStaff.success) {
        setStaff(dataStaff.data);
      } else {
        console.error('Failed to fetch staff:', dataStaff.message);
      }
    } catch (error: any) {
      if (error.response?.status === 404) {
        // No staff found is normal, just set empty array
        setStaff([]);
      } else {
        console.error('Error fetching staff:', error);
      }
    }
  };

  const fetchLotDevices = async (id: string) => {
    try {
      const response = await api.get(`/devices/get-by-lot/${id}`);
      const data = response.data;
      if (data.success) {
        setLotDevices(data.data || []);
        console.log('Lot devices loaded:', data.data);
      } else {
        console.error('Failed to fetch lot devices:', data.message);
        setLotDevices([]);
      }
    } catch (error: any) {
      if (error.response?.status === 404) {
        // No devices found is normal, just set empty array
        setLotDevices([]);
      } else {
        console.error('Error fetching lot devices:', error);
        setLotDevices([]);
      }
    }
  };

  useEffect(() => {
    if (lotId) {
      fetchLot(lotId);
      fetchOperators(lotId);
      fetchStaff(lotId);
      fetchLotDevices(lotId);
    }
  }, [lotId]);

  const handleFieldChange = (field: keyof LotData, value: string) => {
    setEditableFields((prev) => ({
      ...prev,
      [field]: field === "lotCapacity" ? Number(value) : value,
    }));
    setUnsavedChanges(true);
  };

  const handleBooleanFieldChange = (field: keyof LotData, value: boolean) => {
    console.log(`handleBooleanFieldChange called: field=${field}, value=${value}`);

    // Check if we're enabling a field that requires confirmation
    if (value === true && (field === 'allowBetaAi' || field === 'developmentLot')) {
      let title = '';
      let message = '';

      if (field === 'allowBetaAi') {
        title = 'Confirm Beta AI Enable';
        message = 'This lot will cost Parallel 5 cents per day in API calls once enabled, please confirm.';
      } else if (field === 'developmentLot') {
        title = 'Confirm Development Lot Enable';
        message = 'This will add specific demo features to the operator portal including the generate data button please confirm.';
      }

      setConfirmationModalData({
        field,
        value,
        title,
        message
      });
      setShowConfirmationModal(true);
      return;
    }

    // For disabling or other fields, proceed normally
    setEditableFields((prev) => {
      const newFields = {
        ...prev,
        [field]: value,
      };
      console.log(`Updated editableFields:`, newFields);
      return newFields;
    });
    setUnsavedChanges(true);
  };

  const handleConfirmationModalConfirm = () => {
    if (confirmationModalData) {
      setEditableFields((prev) => {
        const newFields = {
          ...prev,
          [confirmationModalData.field]: confirmationModalData.value,
        };
        console.log(`Updated editableFields after confirmation:`, newFields);
        return newFields;
      });
      setUnsavedChanges(true);
    }
    setShowConfirmationModal(false);
    setConfirmationModalData(null);
  };

  const handleConfirmationModalCancel = () => {
    setShowConfirmationModal(false);
    setConfirmationModalData(null);
  };

  const toggleEditMode = () => {
    if (editMode && unsavedChanges) {
      setShowModal(true);
    } else {
      if (!editMode && lot) {
        // Entering edit mode - parse the current address
        parseAddressForEditing(lot.address || "");
      }
      setEditMode(!editMode);
    }
  };

  const checkOwnerExists = async (userId: string): Promise<boolean> => {
    try {
      const response = await api.get(`/users/get-user-by-id/${userId}`);
      const data = response.data;
      return data.success;
    } catch (error) {
      console.error('Error checking owner exists:', error);
      return false;
    }
  };

  const saveChangesToDB = async () => {
    if (!lot) return;

    if (!editableFields.companyName || !editableFields.address || !editableFields.lotName) {
      setErrorMessage("Fill out all required fields: Company Name, Address, Lot Name.");
      return;
    }

    const validOwnerId = owner?.userId || lot?.owner?.userId || lot?.ownerId || getCurrentUserId();
    const payload = {
      ownerId: validOwnerId,
      companyName: editableFields.companyName,
      address: editableFields.address,
      lotName: editableFields.lotName,
      lotCapacity: editableFields.lotCapacity !== undefined ? editableFields.lotCapacity : lot.lotCapacity,
      hasEvCharging: lot.hasEvCharging,
      modifiedBy: getCurrentUserId(),
      allowBetaAi: editableFields.allowBetaAi !== undefined ? editableFields.allowBetaAi : (lot.allowBetaAi === true),
      developmentLot: editableFields.developmentLot !== undefined ? editableFields.developmentLot : (lot.developmentLot === true)
    };

    try {
      const response = await api.put(`/parkinglots/update-parking-lot/${lot.lotId}`, payload);
      const data = response.data;

      if (data.success) {
        console.log('Lot updated successfully');
        setLot(data.data);
        setUnsavedChanges(false);
        setEditMode(false);
        setErrorMessage(null);
      } else {
        console.error('Failed to update lot:', data.message);
        setErrorMessage('Failed to update lot: ' + data.message);
      }
    } catch (error) {
      console.error('Error updating lot:', error);
      setErrorMessage('Error updating lot. Please try again.');
    }
  };

  const handleSaveChanges = () => {
    saveChangesToDB();
    setShowModal(false);
    if (pendingNavigation) {
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
  };

  const handleCancelChanges = () => {
    if (lot) {
      setEditableFields({
        companyName: lot.companyName,
        address: lot.address,
        lotName: lot.lotName,
        lotCapacity: lot.lotCapacity,
        allowBetaAi: lot.allowBetaAi === true, // Explicitly handle boolean values
        developmentLot: lot.developmentLot === true, // Explicitly handle boolean values
      });
      // Reset address components
      parseAddressForEditing(lot.address || "");
      // Clear address validation errors
      setStreetNumberError(false);
      setStreetNameError(false);
      setCityError(false);
      setProvinceStateError(false);
      setPostalCodeError(false);
    }
    setUnsavedChanges(false);
    setEditMode(false);
    setShowModal(false);
    if (pendingNavigation) {
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
    setErrorMessage(null);
  };

  const toggleAccountStatus = async (action: "paused" | "archived") => {
    if (!lot) {
      console.error('Lot data not available');
      return;
    }

    try {
      // Determine the new status based on current status and action
      let statusValue: string;
      let actionDescription: string;

      if (action === "paused") {
        if (lot.lotStatus === "Paused") {
          statusValue = "Active";
          actionDescription = "unpaused";
        } else {
          statusValue = "Paused";
          actionDescription = "paused";
        }
      } else { // action === "archived"
        if (lot.lotStatus === "Suspended") {
          statusValue = "Active";
          actionDescription = "unarchived";
        } else {
          statusValue = "Suspended";
          actionDescription = "archived";
        }
      }

      const validOwnerId = owner?.userId || lot?.owner?.userId || lot?.ownerId || getCurrentUserId();

      // Send complete payload like the working saveChangesToDB function
      const payload = {
        ownerId: validOwnerId,
        companyName: editableFields.companyName || lot.companyName,
        address: editableFields.address || lot.address,
        lotName: editableFields.lotName || lot.lotName,
        lotCapacity: editableFields.lotCapacity !== undefined ? editableFields.lotCapacity : lot.lotCapacity,
        hasEvCharging: lot.hasEvCharging,
        lotStatus: statusValue,
        modifiedBy: getCurrentUserId(),
        allowBetaAi: editableFields.allowBetaAi !== undefined ? editableFields.allowBetaAi : (lot.allowBetaAi === true),
        developmentLot: editableFields.developmentLot !== undefined ? editableFields.developmentLot : (lot.developmentLot === true)
      };

      const response = await api.put(`/parkinglots/update-parking-lot/${lot.lotId}`, payload);
      const data = response.data;

      if (data.success) {
        console.log(`Lot ${actionDescription} successfully`);
        setLot(prev => prev ? ({ ...prev, lotStatus: statusValue }) : null);
      } else {
        console.error(`Failed to ${actionDescription} lot:`, data.message);
        alert(`Failed to ${actionDescription} lot: ${data.message}`);
      }
    } catch (error) {
      console.error(`Error toggling lot status:`, error);
      alert(`Error updating lot status. Please try again.`);
    }
  };

  const handleNavigation = (path: string) => {
    if (editMode && unsavedChanges) {
      setShowModal(true);
      setPendingNavigation(path);
    } else {
      navigate(path);
    }
  };

  const formatDate = (isoDate: string) => {
    if (!isoDate) return "N/A";
    return new Date(isoDate).toLocaleString();
  };

  // Added helper function to format display lot ID
  const formatDisplayLotId = (displayLotId: string) => {
    if (!displayLotId) return "N/A";
    const last8 = displayLotId.slice(-8);
    if (last8.length === 8) {
      return last8.slice(0, 4) + '-' + last8.slice(4);
    }
    return displayLotId;
  };

  const handleDeleteLot = async () => {
    if (!lot) {
      console.error('Lot data not available');
      return;
    }

    try {
      const currentUserId = getCurrentUserId();
      console.log('Attempting to delete lot:', {
        lotId: lot.lotId,
        currentUserId: currentUserId,
        lotStatus: lot.lotStatus,
        deviceCount: lotDevices.length,
        operatorCount: operators.length,
        staffCount: staff.length
      });

      // The backend currently doesn't support parking lot deletion
      // This is intentional to prevent data integrity issues

      let errorMessage = 'PARKING LOT DELETION NOT SUPPORTED\n\n';
      errorMessage += 'The backend does not currently support deleting parking lots. This is intentional to prevent data integrity issues.\n\n';

      if (lotDevices.length > 0) {
        errorMessage += `Remove ${lotDevices.length} Device(s) First: Go to Device Manager to remove all devices from this lot.\n\n`;
      }

      if (operators.length > 0 || staff.length > 0) {
        errorMessage += `Remove ${operators.length + staff.length} User Assignment(s): Remove all operators and staff from this lot first.\n\n`;
      }

      if (lot.currentOccupancy > 0) {
        errorMessage += `Current Occupancy: This lot has ${lot.currentOccupancy} vehicle(s) currently parked.\n\n`;
      }

      errorMessage += 'Alternative: Keep the lot in "Suspended" status instead of deleting it.\n\n';
      errorMessage += 'Contact Support: If you need this lot permanently removed, please contact the development team.';

      alert(errorMessage);
      return;
    } catch (error: any) {
      console.error('Error deleting lot:', error);

      // Enhanced error handling to get more details
      let errorMessage = 'Error deleting lot. Please try again.';

      if (error.response) {
        // Server responded with error status
        const { status, data } = error.response;
        console.error('Server error details:', {
          status,
          data,
          headers: error.response.headers
        });

        if (data && data.message) {
          errorMessage = `Failed to delete lot: ${data.message}`;
        } else if (status === 500) {
          let additionalInfo = '';
          if (lotDevices.length > 0) {
            additionalInfo += `\n\nThis lot has ${lotDevices.length} device(s) that may need to be removed first. Go to Device Manager to remove devices before deleting the lot.`;
          }
          if (operators.length > 0 || staff.length > 0) {
            additionalInfo += `\n\nThis lot has ${operators.length} operator(s) and ${staff.length} staff member(s) assigned.`;
          }
          errorMessage = `Server error occurred while deleting lot. The lot may have associated data that prevents deletion.${additionalInfo}`;
        } else if (status === 403) {
          errorMessage = 'Permission denied. You may not have the required permissions to delete this lot.';
        } else if (status === 404) {
          errorMessage = 'Lot not found or already deleted.';
        } else {
          errorMessage = `Failed to delete lot (Error ${status}). Please try again.`;
        }
      } else if (error.request) {
        // Network error
        errorMessage = 'Network error. Please check your connection and try again.';
      } else {
        // Other error
        errorMessage = error.message || 'Unknown error occurred while deleting lot.';
      }

      alert(errorMessage);
    }
  };

  const confirmDelete = () => {
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    handleDeleteLot();
    setShowDeleteModal(false);
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
  };

  const formatUserId = (userId: string): string => {
    return userId;
  };

  const openAddUserModal = (type: "operator" | "staff" | "owner") => {
    if (!lot) {
      setErrorMessage("Lot data not available");
      return;
    }

    // For owner change, avoid accessing lot.owner if it's undefined
    if (type === "owner" && (!owner && !lot.owner)) {
      // It means no owner assigned; that's acceptable. We'll pass empty string as currentOwnerId.
      // Optionally, you could set a default value or handle this case specially.
    }

    setAddUserType(type);
    setShowAddUserModal(true);
  };

  const handleAddUser = async (userId: string) => {
    if (!lot) {
      console.error('Lot data not available');
      return;
    }

    try {
      let response;

      if (addUserType === 'operator') {
        response = await api.post('/parkinglots/add-operator', {
          lotId: lot.lotId,
          userId: userId,
          modifiedBy: getCurrentUserId()
        });
      } else if (addUserType === 'staff') {
        response = await api.post('/parkinglots/add-staff', {
          lotId: lot.lotId,
          userId: userId,
          modifiedBy: getCurrentUserId()
        });
      } else if (addUserType === 'owner') {
        response = await api.post('/parkinglots/change-owner', {
          lotId: lot.lotId,
          userId: userId,
          modifiedBy: getCurrentUserId()
        });
      }

      const data = response?.data;

      if (data?.success) {
        console.log(`${addUserType} added successfully`);
        setShowAddUserModal(false);
        setAddUserType(null);
        // Refresh the appropriate data
        if (addUserType === 'operator') {
          fetchOperators(lot.lotId);
        } else if (addUserType === 'staff') {
          fetchStaff(lot.lotId);
        } else if (addUserType === 'owner') {
          fetchLot(lot.lotId);
        }
      } else {
        console.error(`Failed to add ${addUserType}:`, data?.message);
        alert(`Failed to add ${addUserType}: ${data?.message}`);
      }
    } catch (error: any) {
      console.error(`Error adding ${addUserType}:`, error);
      alert(`Error adding ${addUserType}. Please try again.`);
    }
  };

  const handleDeleteUser = async () => {
    if (!lot || !showDeleteUserModal) {
      console.error('Lot data or delete modal data not available');
      return;
    }

    try {
      const { type: deleteUserType, userId: deleteUserId } = showDeleteUserModal;
      let response;

      if (deleteUserType === 'operator') {
        response = await api.delete('/parkinglots/remove-operator', {
          data: {
            lotId: lot.lotId,
            userId: deleteUserId,
            modifiedBy: getCurrentUserId()
          }
        });
      } else if (deleteUserType === 'staff') {
        response = await api.delete('/parkinglots/remove-staff', {
          data: {
            lotId: lot.lotId,
            userId: deleteUserId,
            modifiedBy: getCurrentUserId()
          }
        });
      }

      const data = response?.data;

      if (data?.success) {
        console.log(`${deleteUserType} removed successfully`);
        setShowDeleteUserModal(null);
        // Refresh the appropriate data
        if (deleteUserType === 'operator') {
          fetchOperators(lot.lotId);
        } else if (deleteUserType === 'staff') {
          fetchStaff(lot.lotId);
        }
      } else {
        console.error(`Failed to remove ${deleteUserType}:`, data?.message);
        alert(`Failed to remove ${deleteUserType}: ${data?.message}`);
      }
    } catch (error: any) {
      console.error('Error removing user:', error);
      alert('Error removing user. Please try again.');
    }
  };

  const handleEditUser = (user: UserData) => {
    if (editMode) {
      setEditingUser(user);
    }
  };

  const handleUserUpdated = () => {
    if (lotId) {
      fetchLot(lotId);
      fetchOperators(lotId);
      fetchStaff(lotId);
    }
    setEditingUser(null);
  };

  if (!lot) {
    return (
      <div className="customer-page">
        <h1>Customer</h1>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="customer-page">
      <h1>Customer</h1>
      {errorMessage && (
        <div className="error-banner">
          <p>{errorMessage}</p>
        </div>
      )}
      <div className="customer-details">
        <div className="customer-row">
          <div className="customer-label">Company Name:</div>
          <div className="customer-value">
            <p
              contentEditable={editMode}
              suppressContentEditableWarning={true}
              className={editMode ? "editable-highlight" : ""}
              onBlur={(e) =>
                handleFieldChange("companyName", e.currentTarget.textContent || "")
              }
              style={{ wordBreak: "break-word" }}
            >
              {editableFields.companyName ?? lot.companyName}
            </p>
          </div>
        </div>

        <div className="customer-row">
          <div className="customer-label">Lot Name:</div>
          <div className="customer-value">
            <p
              contentEditable={editMode}
              suppressContentEditableWarning={true}
              className={editMode ? "editable-highlight" : ""}
              onBlur={(e) =>
                handleFieldChange("lotName", e.currentTarget.textContent || "")
              }
              style={{ wordBreak: "break-word" }}
            >
              {editableFields.lotName ?? lot.lotName}
            </p>
          </div>
        </div>

        <div className="customer-row">
          <div className="customer-label">Lot ID:</div>
          <div className="customer-value">
            <p className={editMode ? "disabled-text" : ""} style={{ wordBreak: "break-all" }}>
              {lot.lotId}
            </p>
          </div>
        </div>

        <div className="customer-row">
          <div className="customer-label">Lot Display ID:</div>
          <div className="customer-value">
            <p className={editMode ? "disabled-text" : ""} style={{ wordBreak: "break-all" }}>
              {lot.displayLotId} ({formatDisplayLotId(lot.displayLotId)})
            </p>
          </div>
        </div>

        {editMode ? (
          <>
            <div className="customer-row">
              <div className="customer-label">Street Number:</div>
              <div className="customer-value">
                <input
                  type="text"
                  className={`address-input ${streetNumberError ? 'invalid' : ''}`}
                  value={streetNumber}
                  maxLength={10}
                  onChange={(e) => handleAddressComponentChange('streetNumber', e.target.value)}
                  placeholder="123"
                />
              </div>
            </div>

            <div className="customer-row">
              <div className="customer-label">Street Name:</div>
              <div className="customer-value">
                <input
                  type="text"
                  className={`address-input ${streetNameError ? 'invalid' : ''}`}
                  value={streetName}
                  maxLength={50}
                  onChange={(e) => handleAddressComponentChange('streetName', e.target.value)}
                  placeholder="Main Street"
                />
              </div>
            </div>

            <div className="customer-row">
              <div className="customer-label">City:</div>
              <div className="customer-value">
                <input
                  type="text"
                  className={`address-input ${cityError ? 'invalid' : ''}`}
                  value={city}
                  maxLength={30}
                  onChange={(e) => handleAddressComponentChange('city', e.target.value)}
                  placeholder="Toronto"
                />
              </div>
            </div>

            <div className="customer-row">
              <div className="customer-label">Country:</div>
              <div className="customer-value">
                <select
                  className="address-dropdown"
                  value={country}
                  onChange={(e) => handleAddressComponentChange('country', e.target.value)}
                >
                  <option value="Canada">Canada</option>
                  <option value="United States">United States</option>
                </select>
              </div>
            </div>

            <div className="customer-row">
              <div className="customer-label">{country === "Canada" ? "Province:" : "State:"}</div>
              <div className="customer-value">
                <select
                  className={`address-dropdown ${provinceStateError ? 'invalid' : ''}`}
                  value={provinceState}
                  onChange={(e) => handleAddressComponentChange('provinceState', e.target.value)}
                >
                  <option value="">Select {country === "Canada" ? "Province" : "State"}</option>
                  {country === "Canada"
                    ? canadianProvinces.map((province) => (
                      <option key={province.value} value={province.value}>{province.label}</option>
                    ))
                    : usStates.map((state) => (
                      <option key={state.value} value={state.value}>{state.label}</option>
                    ))
                  }
                </select>
              </div>
            </div>

            <div className="customer-row">
              <div className="customer-label">Postal Code:</div>
              <div className="customer-value">
                <input
                  type="text"
                  className={`address-input ${postalCodeError ? 'invalid' : ''}`}
                  value={postalCode}
                  maxLength={country === "Canada" ? 7 : 10}
                  onChange={(e) => handleAddressComponentChange('postalCode', e.target.value)}
                  placeholder={country === "Canada" ? "A1A 1A1" : "12345"}
                />
              </div>
            </div>
          </>
        ) : (
          <div className="customer-row">
            <div className="customer-label">Address:</div>
            <div className="customer-value">
              <p style={{ wordBreak: "break-word" }}>
                {editableFields.address ?? lot.address}
              </p>
            </div>
          </div>
        )}

        <div className="customer-row">
          <div className="customer-label">Lot Capacity:</div>
          <div className="customer-value">
            <p
              contentEditable={editMode}
              suppressContentEditableWarning={true}
              className={editMode ? "editable-highlight" : ""}
              onBlur={(e) =>
                handleFieldChange("lotCapacity", e.currentTarget.textContent || "")
              }
            >
              {editableFields.lotCapacity ?? lot.lotCapacity}
            </p>
          </div>
        </div>

        <div className="customer-row">
          <div className="customer-label">Allow Beta AI:</div>
          <div className="customer-value">
            {editMode ? (
              <select
                value={editableFields.allowBetaAi ? "true" : "false"}
                onChange={(e) => handleBooleanFieldChange("allowBetaAi", e.target.value === "true")}
                className="editable-highlight"
              >
                <option value="false">Disabled</option>
                <option value="true">Enabled</option>
              </select>
            ) : (
              <p>{(editableFields.allowBetaAi ?? lot.allowBetaAi ?? false) ? "Enabled" : "Disabled"}</p>
            )}
          </div>
        </div>

        <div className="customer-row">
          <div className="customer-label">Development Lot:</div>
          <div className="customer-value">
            {editMode ? (
              <select
                value={editableFields.developmentLot ? "true" : "false"}
                onChange={(e) => handleBooleanFieldChange("developmentLot", e.target.value === "true")}
                className="editable-highlight"
              >
                <option value="false">Disabled</option>
                <option value="true">Enabled</option>
              </select>
            ) : (
              <p>{(editableFields.developmentLot ?? lot.developmentLot ?? false) ? "Enabled" : "Disabled"}</p>
            )}
          </div>
        </div>

        <div className="customer-row">
          <div className="customer-label">Created On:</div>
          <div className="customer-value">
            <p className={editMode ? "disabled-text" : ""}>
              {formatDate(lot.createdOn)}
            </p>
          </div>
        </div>

        <div className="customer-row">
          <div className="customer-label">Modified On:</div>
          <div className="customer-value">
            <p className={editMode ? "disabled-text" : ""}>
              {formatDate(lot.modifiedOn)}
            </p>
          </div>
        </div>

        <div className="customer-row">
          <div className="customer-label">Coordinates:</div>
          <div className="customer-value">
            <p className={editMode ? "disabled-text" : ""}>
              {lot.lotLatitude != null && lot.lotLongitude != null ? (
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${lot.lotLatitude},${lot.lotLongitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'inherit', textDecoration: 'underline' }}
                >
                  {`${lot.lotLatitude.toFixed(6)}, ${lot.lotLongitude.toFixed(6)}`}
                </a>
              ) : (
                "N/A"
              )}
            </p>
          </div>
        </div>

        <div className="customer-row">
          <div className="customer-label">Address Last Updated:</div>
          <div className="customer-value">
            <p className={editMode ? "disabled-text" : ""}>
              {lot.addressLastUpdatedOn ? formatDate(lot.addressLastUpdatedOn) : "N/A"}
            </p>
          </div>
        </div>
      </div>

      {/* Show address errors below the grid when in edit mode */}
      {editMode && (streetNumberError || streetNameError || cityError || provinceStateError || postalCodeError) && (
        <div className="address-errors" style={{ marginTop: '10px' }}>
          {streetNumberError && streetNumber.trim().length > 0 && (
            <p className="error">Street number must contain only numbers (max 10 characters)</p>
          )}
          {streetNameError && streetName.trim().length > 0 && (
            <p className="error">Street name must be 50 characters or less</p>
          )}
          {cityError && city.trim().length > 0 && (
            <p className="error">City must be 30 characters or less</p>
          )}
          {provinceStateError && (
            <p className="error">Please select a {country === "Canada" ? "province" : "state"}</p>
          )}
          {postalCodeError && postalCode.trim().length > 0 && (
            <p className="error">
              {country === "Canada"
                ? "Invalid Canadian postal code format (e.g., A1A 1A1)"
                : "Invalid ZIP code format (e.g., 12345 or 12345-6789)"
              }
            </p>
          )}
        </div>
      )}
      <h2>Roles</h2>
      <div className="roles-section">
        <div className="roles-row">
          <div className="roles-label">Owner ID:</div>
          <div className="roles-badges">
            <div className="owner-container">
              {owner ? (
                <span className={editMode ? "Badge" : "NoBadge"} style={{ maxWidth: "100%" }}>
                  <span className="user-info" onClick={() => handleEditUser(owner)} style={{ wordBreak: "break-all" }}>
                    {owner.displayUserId} ({owner.email})
                  </span>
                </span>
              ) : (
                <span>Not assigned</span>
              )}
              {editMode && (
                <button className="change-button" onClick={() => openAddUserModal("owner")}>
                  Change
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="roles-row">
          <div className="roles-label">Operator ID's:</div>
          <div className="roles-badges">
            {operators.map((operator) => (
              <span key={operator.userId} className={editMode ? "Badge" : "NoBadge"} style={{ maxWidth: "100%" }}>
                <span className="user-info" onClick={() => handleEditUser(operator)} style={{ wordBreak: "break-all" }}>
                  {operator.displayUserId} ({operator.email})
                </span>
                {editMode && (
                  <button
                    className="delete-badge-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowDeleteUserModal({ type: "operator", userId: operator.userId });
                    }}
                  >
                    ×
                  </button>
                )}
              </span>
            ))}
            {editMode && (
              <button className="addidbtn" onClick={() => openAddUserModal("operator")}>
                <img src="/assets/Plus.svg" alt="Add Operator" />
              </button>
            )}
          </div>
        </div>
        <div className="roles-row">
          <div className="roles-label">Staff ID's:</div>
          <div className="roles-badges">
            {staff.map((staffMember) => (
              <span key={staffMember.userId} className={editMode ? "Badge" : "NoBadge"} style={{ maxWidth: "100%" }}>
                <span className="user-info" onClick={() => handleEditUser(staffMember)} style={{ wordBreak: "break-all" }}>
                  {staffMember.displayUserId} ({staffMember.email})
                </span>
                {editMode && (
                  <button
                    className="delete-badge-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowDeleteUserModal({ type: "staff", userId: staffMember.userId });
                    }}
                  >
                    ×
                  </button>
                )}
              </span>
            ))}
            {editMode && (
              <button className="addidbtn" onClick={() => openAddUserModal("staff")}>
                <img src="/assets/Plus.svg" alt="Add Staff" />
              </button>
            )}
          </div>
        </div>
      </div>
      <h2>Account</h2>
      <div className="account-actions">
        <button
          className={`action-button ${editMode ? "edit-active" : ""}`}
          onClick={toggleEditMode}
          disabled={lot.lotStatus === "Suspended"}
          style={{
            opacity: lot.lotStatus === "Suspended" ? 0.5 : 1,
            pointerEvents: lot.lotStatus === "Suspended" ? "none" : "auto",
          }}
        >
          <img
            className="button-icon"
            src={editMode ? "/assets/EditInverted.svg" : "/assets/Edit.svg"}
            alt="Edit Icon"
          />
          <img className="button-icon-hover" src="/assets/EditInverted.svg" alt="Edit Icon Hover" />
          <span>Edit</span>
        </button>
        <button
          className={`action-button ${lot.lotStatus === "Paused" ? "edit-active" : ""}`}
          onClick={() => toggleAccountStatus("paused")}
          disabled={editMode || lot.lotStatus === "Suspended"}
          style={{
            opacity: editMode || lot.lotStatus === "Suspended" ? 0.5 : 1,
            pointerEvents: editMode || lot.lotStatus === "Suspended" ? "none" : "auto",
          }}
        >
          <img
            className="button-icon"
            src={lot.lotStatus === "Paused" ? "/assets/PauseInverted.svg" : "/assets/Pause.svg"}
            alt="Pause Icon"
          />
          <img className="button-icon-hover" src="/assets/PauseInverted.svg" alt="Pause Icon Hover" />
          <span>{lot.lotStatus === "Paused" ? "Unpause" : "Pause"}</span>
        </button>
        <button
          className={`action-button ${lot.lotStatus === "Suspended" ? "edit-active" : ""}`}
          onClick={() => toggleAccountStatus("archived")}
          disabled={editMode || lot.lotStatus === "Paused"}
          style={{
            opacity: editMode || lot.lotStatus === "Paused" ? 0.5 : 1,
            pointerEvents: editMode || lot.lotStatus === "Paused" ? "none" : "auto",
          }}
        >
          <img
            className="button-icon"
            src={lot.lotStatus === "Suspended" ? "/assets/ArchiveInverted.svg" : "/assets/Archive.svg"}
            alt="Archive Icon"
          />
          <img className="button-icon-hover" src="/assets/ArchiveInverted.svg" alt="Archive Icon Hover" />
          <span>{lot.lotStatus === "Suspended" ? "Unarchive" : "Archive"}</span>
        </button>
        {lot.lotStatus === "Suspended" && (
          <button
            className="action-button"
            onClick={confirmDelete}
            style={{ opacity: 1, pointerEvents: "auto" }}
          >
            <img
              className="button-icon"
              src="/assets/Delete.svg"
              alt="Delete Icon"
            />
            <img className="button-icon-hover" src="/assets/DeleteInverted.svg" alt="Delete Icon Hover" />
            <span>Delete</span>
          </button>
        )}
      </div>
      {showModal && (
        <Modal
          title="Please confirm changes"
          message="You have unsaved modifications. Save changes?"
          onConfirm={handleSaveChanges}
          onCancel={handleCancelChanges}
          confirmText="Save Changes"
          cancelText="Cancel Changes"
        />
      )}
      {showDeleteModal && (
        <Modal
          title="Delete Parking Lot"
          message={`IMPORTANT: The backend does not currently support parking lot deletion.\n\nThis lot has:\n• ${lotDevices.length} device(s) attached\n• ${operators.length + staff.length} user assignment(s)\n• ${lot.currentOccupancy} current occupancy\n\nClicking "Proceed" will show detailed information about why deletion is not available and what alternatives you have.`}
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
          confirmText="Proceed"
          cancelText="Cancel"
        />
      )}
      {showAddUserModal && (
        <AddUser
          isOpen={showAddUserModal}
          onClose={() => {
            setShowAddUserModal(false);
            setAddUserType(null);
          }}
          onConfirm={handleAddUser}
          currentOwnerId={owner ? owner.userId : (lot ? (lot.owner ? lot.owner.userId : '') : '')}
          type={addUserType || "operator"}
          currentOperators={operators}
          currentStaff={staff}
        />
      )}
      {showDeleteUserModal && (
        <Modal
          title={`Remove ${showDeleteUserModal.type}`}
          message={`Are you sure you want to remove this ${showDeleteUserModal.type}?`}
          onConfirm={handleDeleteUser}
          onCancel={() => setShowDeleteUserModal(null)}
          confirmText="Remove"
          cancelText="Cancel"
        />
      )}
      {editingUser && (
        <EditUser
          isOpen={true}
          onClose={() => setEditingUser(null)}
          onSave={handleUserUpdated}
          user={editingUser}
        />
      )}
      {showConfirmationModal && confirmationModalData && (
        <Modal
          title={confirmationModalData.title}
          message={confirmationModalData.message}
          onConfirm={handleConfirmationModalConfirm}
          onCancel={handleConfirmationModalCancel}
          confirmText="Enable"
          cancelText="Leave Disabled"
        />
      )}
    </div>
  );
};

export default Customer;
